/**
 * Notifications Mutations — RTK Query mutation endpoints
 *
 * Injected into notificationsApi via injectEndpoints.
 */
import { supabase } from "@flexwise/shared/lib/supabaseClient";
import type {
  NotificationPreferences,
  PushSubscriptionRecord,
  NotificationHistoryPayload,
} from "../types/notificationTypes";
import {
  notificationsApi,
  type GetPreferencesArgs,
  type UpsertPreferencesArgs,
  type SaveSubscriptionArgs,
  type RemoveSubscriptionArgs,
  type DateActionArgs,
  type NotificationSettings,
  type UpdateSettingsArgs,
} from "./notificationsApi";

const mutationsApi = notificationsApi.injectEndpoints({
  endpoints: (builder) => ({
    upsertPreferences: builder.mutation<
      NotificationPreferences,
      UpsertPreferencesArgs
    >({
      async queryFn({ profileId, schoolId, prefs }) {
        const row = {
          user_profile_id: profileId,
          school_id: schoolId,
          ...prefs,
        };

        const { data, error } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- table not in generated types
          .from("notification_preferences" as any)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- row shape matches DB
          .upsert(row as any, { onConflict: "user_profile_id" })
          .select()
          .single();

        if (error) {
          return { error: { status: "CUSTOM_ERROR", error: error.message } };
        }

        const d: Record<string, unknown> = data;

        return {
          data: {
            user_profile_id: d.user_profile_id as string,
            school_id: d.school_id as string,
            push_enabled: (d.push_enabled as boolean) ?? true,
            email_enabled: (d.email_enabled as boolean) ?? false,
            quiet_days: (d.quiet_days as number[]) ?? [],
            quiet_start: (d.quiet_start as string | null) ?? null,
            quiet_end: (d.quiet_end as string | null) ?? null,
            allow_substitution: (d.allow_substitution as boolean) ?? true,
            allow_calendar: (d.allow_calendar as boolean) ?? true,
            allow_school_info: (d.allow_school_info as boolean) ?? true,
            allow_child_updates: (d.allow_child_updates as boolean) ?? true,
          },
        };
      },
      async onQueryStarted(
        { profileId, schoolId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updated } = await queryFulfilled;
          dispatch(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (notificationsApi.util.updateQueryData as any)(
              "getPreferences",
              { profileId, schoolId },
              () => updated,
            ),
          );
        } catch {
          // Mutation failed — UI handles its own error state
        }
      },
    }),

    saveSubscription: builder.mutation<
      PushSubscriptionRecord,
      SaveSubscriptionArgs
    >({
      async queryFn({
        profileId,
        schoolId,
        endpoint,
        p256dh,
        auth_key,
        user_agent,
      }) {
        const { data, error } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- table not in generated types
          .from("push_subscriptions" as any)
          .upsert(
            {
              user_profile_id: profileId,
              school_id: schoolId,
              endpoint,
              p256dh,
              auth_key,
              user_agent,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- row shape matches DB
            } as any,
            { onConflict: "user_profile_id,endpoint" },
          )
          .select(
            "id, user_profile_id, school_id, endpoint, user_agent, created_at",
          )
          .single();

        if (error) {
          return { error: { status: "CUSTOM_ERROR", error: error.message } };
        }

        return { data };
      },
      async onQueryStarted(
        { profileId, schoolId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: newSub } = await queryFulfilled;
          dispatch(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (notificationsApi.util.updateQueryData as any)(
              "getSubscriptions",
              { profileId, schoolId },
              (draft: PushSubscriptionRecord[]) => {
                const idx = draft.findIndex((s) => s.id === newSub.id);
                if (idx >= 0) {
                  draft[idx] = newSub;
                } else {
                  draft.unshift(newSub);
                }
              },
            ),
          );
        } catch {
          // Mutation failed
        }
      },
    }),

    removeSubscription: builder.mutation<
      { id: string },
      RemoveSubscriptionArgs
    >({
      async queryFn({ subscriptionId }) {
        const { error } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- table not in generated types
          .from("push_subscriptions" as any)
          .delete()
          .eq("id", subscriptionId);

        if (error) {
          return { error: { status: "CUSTOM_ERROR", error: error.message } };
        }

        return { data: { id: subscriptionId } };
      },
      invalidatesTags: ["Subscriptions"],
    }),

    // ── Queue Control ─────────────────────────────────────────────────────

    scheduleNotification: builder.mutation<unknown, DateActionArgs>({
      async queryFn({ date }) {
        const { data, error } = await supabase.functions.invoke(
          "send-substitution-notifications",
          { body: { action: "schedule", date } },
        );
        if (error) return { error: { status: 500, data: error.message } };
        return { data };
      },
      invalidatesTags: (_res, _err, { date }) => [
        { type: "QueueStatus", id: date },
        // Underlying substitution state just changed — the previously
        // cached preview ("recipients", "has_changes" per teacher) is
        // now stale. Without this, the banner shows
        // "Keine Lehrkräfte betroffen" even after fresh publishes.
        { type: "NotificationPreview", id: date },
      ],
    }),

    sendNotificationNow: builder.mutation<unknown, DateActionArgs>({
      async queryFn({ date }) {
        const { data, error } = await supabase.functions.invoke(
          "send-substitution-notifications",
          { body: { action: "send_now", date } },
        );
        if (error) return { error: { status: 500, data: error.message } };
        return { data };
      },
      invalidatesTags: (_res, _err, { date }) => [
        { type: "QueueStatus", id: date },
        { type: "DailyDeliveries", id: date },
        { type: "NotificationPreview", id: date },
      ],
    }),

    pauseNotification: builder.mutation<unknown, DateActionArgs>({
      async queryFn({ date }) {
        const { data, error } = await supabase.functions.invoke(
          "send-substitution-notifications",
          { body: { action: "pause", date } },
        );
        if (error) return { error: { status: 500, data: error.message } };
        return { data };
      },
      invalidatesTags: (_res, _err, { date }) => [
        { type: "QueueStatus", id: date },
      ],
    }),

    resumeNotification: builder.mutation<unknown, DateActionArgs>({
      async queryFn({ date }) {
        const { data, error } = await supabase.functions.invoke(
          "send-substitution-notifications",
          { body: { action: "resume", date } },
        );
        if (error) return { error: { status: 500, data: error.message } };
        return { data };
      },
      invalidatesTags: (_res, _err, { date }) => [
        { type: "QueueStatus", id: date },
      ],
    }),

    retryNotification: builder.mutation<unknown, DateActionArgs>({
      async queryFn({ date }) {
        const { data, error } = await supabase.functions.invoke(
          "send-substitution-notifications",
          { body: { action: "retry", date } },
        );
        if (error) return { error: { status: 500, data: error.message } };
        return { data };
      },
      invalidatesTags: (_res, _err, { date }) => [
        { type: "QueueStatus", id: date },
      ],
    }),

    cancelNotification: builder.mutation<unknown, DateActionArgs>({
      async queryFn({ date }) {
        const { data, error } = await supabase.functions.invoke(
          "send-substitution-notifications",
          { body: { action: "cancel", date } },
        );
        if (error) return { error: { status: 500, data: error.message } };
        return { data };
      },
      invalidatesTags: (_res, _err, { date }) => [
        { type: "QueueStatus", id: date },
        { type: "NotificationPreview", id: date },
      ],
    }),

    // ── Settings ──────────────────────────────────────────────────────────

    updateNotificationSettings: builder.mutation<
      NotificationSettings,
      UpdateSettingsArgs
    >({
      async queryFn({ schoolId, sendDelayMinutes }) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase as any)
          .from("notification_settings")
          .update({
            send_delay_minutes: sendDelayMinutes,
            updated_at: new Date().toISOString(),
          })
          .eq("school_id", schoolId)
          .select()
          .single();

        if (error) return { error: { status: 500, data: error.message } };
        return { data: data as NotificationSettings };
      },
      invalidatesTags: ["Settings"],
    }),

    // ── Mark Read (Phase 6e.1 — queue_id + delivery_id based) ────────────
    // Pessimistic cache patch: patches cache AFTER successful mutation.
    // No invalidation/refetch — zero extra network requests.
    // Args: { queueIds: string[], deliveryIds: string[] }
    markNotificationsRead: builder.mutation<void, { queueIds: string[]; deliveryIds: string[] }>({
      async queryFn({ queueIds, deliveryIds }) {
        const { error } = await supabase.rpc(
          "fn_notifications_mark_read" as any,
          { p_queue_ids: queueIds, p_delivery_ids: deliveryIds } as any,
        );

        if (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error.message,
              data: error,
            },
          };
        }

        return { data: undefined };
      },
      async onQueryStarted({ queueIds, deliveryIds }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (notificationsApi.util.updateQueryData as any)(
              "getMyNotificationHistory",
              undefined,
              (draft: NotificationHistoryPayload) => {
                const queueIdSet = new Set(queueIds);
                const deliveryIdSet = new Set(deliveryIds);
                let readCount = 0;
                for (const item of draft.items) {
                  if (item.read_at) continue;
                  const matchQueue = item.queue_id && queueIdSet.has(item.queue_id);
                  const matchDelivery = deliveryIdSet.has(item.id);
                  if (matchQueue || matchDelivery) {
                    item.read_at = new Date().toISOString();
                    readCount++;
                  }
                }
                draft.unread_count = Math.max(
                  0,
                  draft.unread_count - readCount,
                );
              },
            ),
          );
        } catch {
          // Fallback: invalidate to refetch if patch fails
          dispatch(
            notificationsApi.util.invalidateTags(["NotificationHistory"]),
          );
        }
      },
    }),

    /**
     * Soft-dismiss notifications for the caller (sets dismissed_at = now()).
     * Mirrors the auth model of markNotificationsRead — the RPC ignores rows
     * that don't belong to the current user. Used by the trash icon in the
     * bell popover to actually clear the visible list, not just mark it read.
     */
    dismissNotifications: builder.mutation<
      void,
      { queueIds: string[]; deliveryIds: string[] }
    >({
      async queryFn({ queueIds, deliveryIds }) {
        const { error } = await supabase.rpc(
          "fn_notifications_dismiss" as any,
          { p_queue_ids: queueIds, p_delivery_ids: deliveryIds } as any,
        );
        if (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error.message,
              data: error,
            },
          };
        }
        return { data: undefined };
      },
      async onQueryStarted({ queueIds, deliveryIds }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (notificationsApi.util.updateQueryData as any)(
              "getMyNotificationHistory",
              undefined,
              (draft: NotificationHistoryPayload) => {
                const queueIdSet = new Set(queueIds);
                const deliveryIdSet = new Set(deliveryIds);
                let removedUnread = 0;
                draft.items = draft.items.filter((item) => {
                  const matchQueue = item.queue_id && queueIdSet.has(item.queue_id);
                  const matchDelivery = deliveryIdSet.has(item.id);
                  const drop = matchQueue || matchDelivery;
                  if (drop && !item.read_at) removedUnread++;
                  return !drop;
                });
                draft.unread_count = Math.max(
                  0,
                  draft.unread_count - removedUnread,
                );
              },
            ),
          );
        } catch {
          dispatch(
            notificationsApi.util.invalidateTags(["NotificationHistory"]),
          );
        }
      },
    }),

    excludeTeacher: builder.mutation<
      unknown,
      { date: string; teacher_id: string }
    >({
      async queryFn({ date, teacher_id }) {
        const { data, error } = await supabase.functions.invoke(
          "send-substitution-notifications",
          { body: { action: "exclude_teacher", date, teacher_id } },
        );
        if (error) return { error: { status: 500, data: error.message } };
        return { data };
      },
      invalidatesTags: (_res, _err, { date }) => [
        { type: "QueueStatus", id: date },
      ],
    }),

    includeTeacher: builder.mutation<
      unknown,
      { date: string; teacher_id: string }
    >({
      async queryFn({ date, teacher_id }) {
        const { data, error } = await supabase.functions.invoke(
          "send-substitution-notifications",
          { body: { action: "include_teacher", date, teacher_id } },
        );
        if (error) return { error: { status: 500, data: error.message } };
        return { data };
      },
      invalidatesTags: (_res, _err, { date }) => [
        { type: "QueueStatus", id: date },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useUpsertPreferencesMutation,
  useSaveSubscriptionMutation,
  useRemoveSubscriptionMutation,
  useScheduleNotificationMutation,
  useSendNotificationNowMutation,
  usePauseNotificationMutation,
  useResumeNotificationMutation,
  useRetryNotificationMutation,
  useCancelNotificationMutation,
  useUpdateNotificationSettingsMutation,
  useMarkNotificationsReadMutation,
  useDismissNotificationsMutation,
  useExcludeTeacherMutation,
  useIncludeTeacherMutation,
} = mutationsApi;
